import React, { useEffect, useMemo, useState } from 'react'
import { useFormikContext } from 'formik'
import { makeStyles } from 'tss-react/mui'
import { Box, Button, useTheme } from '@mui/material'
import moment, { Moment } from '@e3dc-react/shell/Libs/moment'

import SeparatorLine from 'src/Elements/Separator/SeparatorLine'
import FormikSelect from 'src/Libs/inputs/formik/Select'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { TextField, TextEditor, SwitchField } from 'src/Libs/inputs/formik'
import { Select } from '@e3dc-react/shell/Elements/Select'
import { DatePicker } from '@e3dc-react/shell/Elements/InputFields'
import { PLATFORM_OPTIONS, NOTIFICATION_TYPE_OPTIONS, NOTIFICATION_USER_LEVEL_OPTIONS } from 'src/Libs/notifications/constants'
import { NotificationForm as NotificationFormModel } from '../models/notifications.model'
import { NotificationUserLevel } from '../types'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { uploadImageForNotification } from 'src/Libs/notifications/api/notifications.api'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'

import { ApiResult, usePortalMutation } from 'src/Libs/api'

const useStyles = makeStyles()(theme => ({
  form: {
    marginTop: theme.spacing(2),
    paddingInline: theme.spacing(3),
  },
  container: {
    display: 'grid',
    gridTemplateColumns: '50% 50%',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'end',
    marginTop: theme.spacing(2),
    width: '100%',
  },
  select: {
    '& > div > div > div > p': {
      fontSize: theme.shape.select.fontSize,
    },
  },
  datePicker: {
    '& .MuiInputBase-root': {
      fontSize: theme.shape.inputField.fontSize,
    },
  },
}))

/**
 * Component that holds the form to create / edit notification (Message of the Day)
 */
const NotificationForm: React.FC = () => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const { handleSubmit, setFieldValue, setValues, values, errors, touched } = useFormikContext<NotificationFormModel>()
  const today = moment()
  const startDate = moment(values.startAt)
  const authFetch = useAuthFetchWithResponse()
  const [newImageIds, setNewImageIds] = useState<string[]>([])
  const { enqueueErrorStack } = useSnackbar()

  useEffect(() => {
    setFieldValue('fileKeys', newImageIds)
  }, [newImageIds, setFieldValue])

  const getMinStartDate = (): Moment => {
    if (!values.startAt) return today

    if (startDate.isAfter(today)) {
      return today
    } else {
      return startDate
    }
  }

  const platformOptionsToDisplay = useMemo(() => {
    return PLATFORM_OPTIONS.map(option => ({ ...option, label: t(option.label) }))
  }, [t])

  const typeOptionsToDisplay = useMemo(() => {
    return NOTIFICATION_TYPE_OPTIONS.map(option => ({ ...option, label: t(option.label) }))
  }, [t])

  const userLevelOptionsToDisplay = useMemo(() => {
    return NOTIFICATION_USER_LEVEL_OPTIONS.map(option => ({ ...option, label: t(option.label) }))
  }, [t])

  const handleChangeUserLevel = (value: NotificationUserLevel): void => {
    switch (value) {
      case 'ALL':
        /* Installer, Partner, End User */
        setValues(prev => ({ ...prev, user: true, installer: true, partner: true }))
        setFieldValue('userLevel', value)
        return
      case 'USER':
        /* only End User */
        setValues(prev => ({ ...prev, user: true, installer: false, partner: false }))
        setFieldValue('userLevel', value)
        return
      case 'PARTNER_AND_INSTALLER':
        /* Partner and Installer */
        setValues(prev => ({ ...prev, user: false, installer: true, partner: true }))
        setFieldValue('userLevel', value)
        return
      case 'INTERNAL':
        setValues(prev => ({ ...prev, user: false, installer: false, partner: false }))
        setFieldValue('userLevel', value)
        return
      default:
        return
    }
  }

  const { mutateAsync: handleImageUploadMutation, isPending: isLoading } = usePortalMutation<
    { url: string; fileKey: string },
    Error,
    File | undefined
  >({
    mutationFn: async (file: File | undefined) => {
      if (!file) {
        return new ApiResult<{ url: string; fileKey: string }>(null)
      }

      const formData = new FormData()
      formData.append('file', file)

      const response = await uploadImageForNotification(formData, authFetch)
      const key = response.value?.fileKey
      const url = response.value?.url

      if (url && key) {
        setNewImageIds(prev => [...prev, key])
        return new ApiResult({ url, fileKey: key })
      }

      throw new Error('Image upload failed')
    },
    onError: error => {
      enqueueErrorStack(t('notifications.form.image-upload-error'))
      console.error(error)
    },
  })

  const handleImageUpload = async (file: File | undefined): Promise<{ url: string; fileKey: string } | undefined> => {
    return (await handleImageUploadMutation(file)) ?? undefined
  }

  return (
    <form className={classes.form} onSubmit={handleSubmit}>
      <Box className={classes.container} gap={2} mb={2}>
        <Box display={'flex'} flexDirection={'column'} gap={2}>
          <TextField label={t('notifications.table.title')} name="title" />
          <TextEditor label={t(`notifications.table.text`)} name="text" onImageDropped={handleImageUpload} isLoading={isLoading} />
        </Box>
        <Box display={'flex'} flexDirection={'column'} gap={2} alignItems={'flex-end'}>
          <DatePicker
            label={t('notifications.table.startAt')}
            value={values.startAt}
            format={t('common.format.date')}
            onChange={value => {
              setFieldValue('startAt', moment.parseZone(value).format('yyyy-MM-DD'))
            }}
            helperText={touched.startAt ? errors.startAt : undefined}
            clearable={false}
            minDate={getMinStartDate()}
            iconPaddingRight={theme.spacing(2)}
            className={classes.datePicker}
          />
          <DatePicker
            label={t('notifications.table.stopAt')}
            value={values.stopAt}
            format={t('common.format.date')}
            onChange={value => setFieldValue('stopAt', moment(value).endOf('day'))}
            helperText={touched.stopAt ? errors.stopAt : undefined}
            clearable={false}
            minDate={values.startAt ? startDate : today}
            iconPaddingRight={theme.spacing(2)}
            className={classes.datePicker}
          />
          <FormikSelect label={t('notifications.table.platform')} name="platform" options={platformOptionsToDisplay} />
          <Select
            label={t('common.userLevel')}
            name="userLevel"
            options={userLevelOptionsToDisplay}
            value={values.userLevel}
            onChange={value => handleChangeUserLevel(value)}
            className={classes.select}
          />
          <FormikSelect label={t('notifications.table.type')} name="type" options={typeOptionsToDisplay} />
          <SwitchField label={t('notifications.table.isActive')} name={'active'} />
        </Box>
      </Box>
      <SeparatorLine />
      <Box className={classes.buttonRow}>
        <Button type="submit" variant="contained" size="large" color="primary">
          {t('common.save')}
        </Button>
      </Box>
    </form>
  )
}

export default NotificationForm
